import {
  clearSession as clearStoredSession,
  getSession as getStoredSession,
  setAccessToken,
  setSession as setStoredSession,
  type StoredSession,
} from '@/lib/storage/local';
import { logger } from '@/utils/logger';

export type { StoredSession };

/** Renew 30s early, so a token cannot expire while a slow request is in flight. */
const EXPIRY_SKEW_MS = 30_000;

/** Reads `exp` without checking the signature — the server does that; we only need the time. */
function readExpiry(token: string): number | undefined {
  const payload = token.split('.')[1];
  if (payload === undefined) return undefined;

  try {
    const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims: unknown = JSON.parse(atob(padded));

    if (typeof claims !== 'object' || claims === null || !('exp' in claims)) return undefined;

    const { exp } = claims;
    return typeof exp === 'number' ? exp : undefined;
  } catch {
    return undefined;
  }
}

/** An unreadable token counts as expired: one wasted refresh beats a certain 401. */
export function isAccessTokenExpired(session: StoredSession): boolean {
  const exp = readExpiry(session.accessToken);
  if (exp === undefined) return true;

  return Date.now() >= exp * 1000 - EXPIRY_SKEW_MS;
}

export function getSession(): StoredSession | undefined {
  return getStoredSession();
}

/** Ignores expiry on purpose: an expired access token is refreshed, not a sign-out. */
export function hasSession(): boolean {
  return getStoredSession() !== undefined;
}

export function startSession(session: StoredSession): void {
  setStoredSession(session);
}

export function updateAccessToken(accessToken: string): void {
  setAccessToken(accessToken);
}

export function endSession(): void {
  clearStoredSession();
}

/** Set by AuthContext: `lib/` cannot import the router, so it is told when to leave. */
let onSessionExpired: (() => void) | undefined;

export function setSessionExpiredHandler(handler: (() => void) | undefined): void {
  onSessionExpired = handler;
}

export function expireSession(): void {
  if (!hasSession()) return;

  logger.warn('auth: session expired');
  endSession();
  onSessionExpired?.();
}
